import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../common/Modal";
import LoadingSpinner from "../common/LoadingSpinner";
import apiClient from "../../services/apiClient";

const AnalyzeMissingSectionsModal = ({ isOpen, onClose, documentId, onSectionsGenerated }) => {
  const [step, setStep] = useState("select-model"); // select-model, analyzing, results, generating
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [modelsLoading, setModelsLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [selectedSections, setSelectedSections] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, currentSection: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      loadAvailableModels();
      // Reset state when modal opens
      setStep("select-model");
      setSelectedModel("");
      setAnalysis(null);
      setSelectedSections([]);
      setError("");
    }
  }, [isOpen]);

  const loadAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const response = await apiClient.getAIModels();
      const models = response.data?.models || response.models || [];
      setAvailableModels(models);

      const defaultModel = models.find((m) => m.isDefault);
      if (defaultModel) {
        setSelectedModel(defaultModel.model);
      }
    } catch (error) {
      console.error("Failed to load available models:", error);
      setError("Failed to load AI models");
    } finally {
      setModelsLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedModel) {
      setError("Please select an AI model");
      return;
    }

    try {
      setAnalyzing(true);
      setError("");
      setStep("analyzing");

      const response = await apiClient.analyzeMissingSections(documentId, selectedModel);

      if (response.success && response.data.analysis) {
        setAnalysis(response.data.analysis);
        setStep("results");
      } else {
        throw new Error("Invalid response from analysis");
      }
    } catch (error) {
      console.error("Analysis failed:", error);
      setError(error.response?.data?.message || error.message || "Analysis failed");
      setStep("select-model");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleToggleSection = (index) => {
    setSelectedSections(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleGenerateSections = async () => {
    if (selectedSections.length === 0) {
      setError("Please select at least one section to generate");
      return;
    }

    try {
      setGenerating(true);
      setError("");
      setStep("generating");

      const sectionsToGenerate = selectedSections.map(index => analysis.missingSections[index]);

      for (let i = 0; i < sectionsToGenerate.length; i++) {
        const section = sectionsToGenerate[i];
        setGenerationProgress({
          current: i + 1,
          total: sectionsToGenerate.length,
          currentSection: section.sectionTitle
        });

        const sectionSpec = {
          sectionTitle: section.sectionTitle,
          description: section.description,
          insertPosition: section.insertPosition || "end",
          rfpReference: section.rfpReference,
          estimatedWords: section.estimatedWords || 800,
          selectedModel: selectedModel
        };

        await apiClient.generateSection(documentId, sectionSpec);
      }

      // Success!
      onSectionsGenerated();
      onClose();
    } catch (error) {
      console.error("Section generation failed:", error);
      setError(error.response?.data?.message || error.message || "Generation failed");
      setStep("results");
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedSections.length === analysis.missingSections.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(analysis.missingSections.map((_, index) => index));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Analyze & Add Missing Sections" size="4xl">
      <div className="space-y-4">
        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <FontAwesomeIcon icon="exclamation-circle" className="text-red-600 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Step 1: Select Model */}
        {step === "select-model" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select an AI model to analyze your proposal document and identify any missing sections required by the RFP.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AI Model
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={modelsLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {modelsLoading ? (
                  <option value="">Loading models...</option>
                ) : (
                  <>
                    <option value="">Select AI Model...</option>
                    <optgroup label="OpenAI">
                      {availableModels
                        .filter((m) => m.provider === "openai")
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Anthropic">
                      {availableModels
                        .filter((m) => m.provider === "anthropic")
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Google">
                      {availableModels
                        .filter((m) => m.provider === "google")
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name}
                          </option>
                        ))}
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAnalyze}
                disabled={!selectedModel || modelsLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <FontAwesomeIcon icon="search" />
                <span>Analyze Document</span>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Analyzing */}
        {step === "analyzing" && (
          <div className="py-12 text-center">
            <LoadingSpinner size="lg" text="Analyzing document for missing sections..." />
            <p className="text-sm text-gray-500 mt-4">
              This may take a minute as the AI reviews the RFP requirements and compares them to your existing sections.
            </p>
          </div>
        )}

        {/* Step 3: Results */}
        {step === "results" && analysis && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FontAwesomeIcon icon="info-circle" className="text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900 mb-1">Analysis Complete</h4>
                  <p className="text-sm text-blue-800">{analysis.analysis}</p>
                  {analysis.completenessScore && (
                    <p className="text-sm text-blue-800 mt-1">
                      <strong>Completeness Score:</strong> {analysis.completenessScore}%
                    </p>
                  )}
                </div>
              </div>
            </div>

            {analysis.missingSections.length === 0 ? (
              <div className="text-center py-8">
                <FontAwesomeIcon icon="check-circle" className="text-green-500 text-4xl mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Missing Sections</h3>
                <p className="text-gray-600">Your proposal appears to be complete!</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">
                    Found {analysis.missingSections.length} Missing Section{analysis.missingSections.length !== 1 ? 's' : ''}
                  </h4>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {selectedSections.length === analysis.missingSections.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {analysis.missingSections.map((section, index) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedSections.includes(index)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleToggleSection(index)}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedSections.includes(index)}
                          onChange={() => handleToggleSection(index)}
                          className="mt-1"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <h5 className="font-medium text-gray-900">{section.sectionTitle}</h5>
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                section.reason === 'Required'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {section.reason}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{section.description}</p>
                          {section.rfpReference && (
                            <p className="text-xs text-gray-500 mt-2">
                              <FontAwesomeIcon icon="file-alt" className="mr-1" />
                              {section.rfpReference}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateSections}
                    disabled={selectedSections.length === 0}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <FontAwesomeIcon icon="plus-circle" />
                    <span>Generate {selectedSections.length} Section{selectedSections.length !== 1 ? 's' : ''}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 4: Generating */}
        {step === "generating" && (
          <div className="py-12 text-center">
            <LoadingSpinner size="lg" />
            <p className="text-lg font-medium text-gray-900 mt-4">
              Generating section {generationProgress.current} of {generationProgress.total}
            </p>
            <p className="text-sm text-gray-600 mt-2">{generationProgress.currentSection}</p>
            <div className="mt-6 max-w-md mx-auto">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(generationProgress.current / generationProgress.total) * 100}%`
                  }}
                ></div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              This may take a few minutes depending on the number of sections and model selected.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AnalyzeMissingSectionsModal;
